import React, { useState, useRef, useEffect } from 'react';
import { useTheme, THEMES, PRIMARY_COLORS } from '../../contexts/ThemeContext';
import './ThemeColorSelector.css';

const ThemeColorSelector = () => {
  const { theme, changeTheme, primaryColor, changePrimaryColor } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('theme'); // 'theme' or 'color'
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customColor, setCustomColor] = useState('#10b981');
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const dropdownRef = useRef(null);

  // Listen for language changes
  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Translations
  const translations = {
    EN: {
      theme: 'Theme',
      color: 'Color',
      selectTheme: 'Select theme',
      selectColor: 'Select color',
      customColor: 'Custom Color',
      selectThemeOption: (themeName) => `Select ${themeName} theme`,
      selectColorOption: (colorName) => `Select ${colorName} color`
    },
    KU: {
      theme: 'دۆخ',
      color: 'ڕەنگ',
      selectTheme: 'دۆخ هەڵبژێرە',
      selectColor: 'ڕەنگ هەڵبژێرە',
      customColor: 'ڕەنگی تایبەت',
      selectThemeOption: (themeName) => `دۆخی ${themeName} هەڵبژێرە`,
      selectColorOption: (colorName) => `ڕەنگی ${colorName} هەڵبژێرە`
    },
    AR: {
      theme: 'المظهر',
      color: 'اللون',
      selectTheme: 'اختر المظهر',
      selectColor: 'اختر اللون',
      customColor: 'لون مخصص',
      selectThemeOption: (themeName) => `اختر مظهر ${themeName}`,
      selectColorOption: (colorName) => `اختر لون ${colorName}`
    },
    TR: {
      theme: 'Tema',
      color: 'Renk',
      selectTheme: 'Tema seç',
      selectColor: 'Renk seç',
      customColor: 'Özel Renk',
      selectThemeOption: (themeName) => `${themeName} temasını seç`,
      selectColorOption: (colorName) => `${colorName} rengini seç`
    }
  };

  const t = translations[currentLang] || translations.EN;

  // Get translated theme name
  const getThemeName = (themeKey) => {
    const themeOption = THEMES[themeKey];
    if (themeOption && themeOption.translations) {
      return themeOption.translations[currentLang] || themeOption.name;
    }
    return themeOption?.name || themeKey;
  };

  // Get translated color name
  const getColorName = (colorKey) => {
    const color = PRIMARY_COLORS[colorKey];
    if (color && color.translations) {
      return color.translations[currentLang] || color.name;
    }
    return color?.name || colorKey;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleThemeSelect = (themeKey) => {
    changeTheme(themeKey);
  };

  const handleColorSelect = (colorKey) => {
    changePrimaryColor(colorKey);
    setShowCustomPicker(false);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    changePrimaryColor(color);
  };

  // Get current color display
  const getCurrentColorDisplay = () => {
    if (primaryColor.name === 'Custom') {
      return primaryColor.color;
    }
    const presetKey = Object.keys(PRIMARY_COLORS).find(
      key => PRIMARY_COLORS[key].color === primaryColor.color
    );
    return presetKey || 'emerald';
  };

  const currentTheme = THEMES[theme] || THEMES.light;
  const currentThemeName = getThemeName(theme);
  const currentColorKey = getCurrentColorDisplay();

  return (
    <div className="theme-color-selector" ref={dropdownRef}>
      <button
        className="theme-color-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t.selectTheme}
        aria-expanded={isOpen}
        title={t.selectTheme}
      >
        <span className="theme-color-icon">{currentTheme.icon}</span>
        <span className="theme-color-name">{currentThemeName}</span>
        <svg 
          className={`theme-color-arrow ${isOpen ? 'open' : ''}`}
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="none"
        >
          <path 
            d="M4 6L8 10L12 6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="theme-color-dropdown">
          {/* Tabs */}
          <div className="theme-color-tabs">
            <button
              className={`theme-color-tab ${activeTab === 'theme' ? 'active' : ''}`}
              onClick={() => setActiveTab('theme')}
            >
              {t.theme}
            </button>
            <button
              className={`theme-color-tab ${activeTab === 'color' ? 'active' : ''}`}
              onClick={() => setActiveTab('color')}
            >
              {t.color}
            </button>
          </div>

          {/* Theme Content */}
          {activeTab === 'theme' && (
            <div className="theme-color-content">
              {Object.entries(THEMES).map(([key, themeOption]) => {
                const translatedName = getThemeName(key);
                return (
                  <button
                    key={key}
                    className={`theme-color-option ${theme === key ? 'active' : ''}`}
                    onClick={() => handleThemeSelect(key)}
                    aria-label={t.selectThemeOption(translatedName)}
                  >
                    <span className="theme-color-option-icon">{themeOption.icon}</span>
                    <span className="theme-color-option-name">{translatedName}</span>
                    {theme === key && (
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none"
                        className="theme-color-check"
                      >
                        <path 
                          d="M13.3333 4L6 11.3333L2.66667 8" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Color Content */}
          {activeTab === 'color' && (
            <div className="theme-color-content">
              <div className="color-presets">
                {Object.entries(PRIMARY_COLORS).map(([key, colorOption]) => {
                  const translatedName = getColorName(key);
                  return (
                    <button
                      key={key}
                      className={`theme-color-option ${currentColorKey === key ? 'active' : ''}`}
                      onClick={() => handleColorSelect(key)}
                      aria-label={t.selectColorOption(translatedName)}
                    >
                      <span 
                        className="color-option-preview" 
                        style={{ backgroundColor: colorOption.color }}
                      />
                      <span className="theme-color-option-name">{translatedName}</span>
                      {currentColorKey === key && (
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 16 16" 
                          fill="none"
                          className="theme-color-check"
                        >
                          <path 
                            d="M13.3333 4L6 11.3333L2.66667 8" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="color-divider" />
              
              <div className="custom-color-section">
                <button
                  className="custom-color-toggle"
                  onClick={() => setShowCustomPicker(!showCustomPicker)}
                >
                  <span>{t.customColor}</span>
                  <svg 
                    className={`custom-arrow ${showCustomPicker ? 'open' : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none"
                  >
                    <path 
                      d="M4 6L8 10L12 6" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                
                {showCustomPicker && (
                  <div className="custom-color-picker">
                    <input
                      type="color"
                      value={primaryColor.name === 'Custom' ? primaryColor.color : customColor}
                      onChange={handleCustomColorChange}
                      className="color-input"
                    />
                    <input
                      type="text"
                      value={primaryColor.name === 'Custom' ? primaryColor.color : customColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomColor(value);
                        if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                          changePrimaryColor(value);
                        }
                      }}
                      className="color-text-input"
                      placeholder="#000000"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThemeColorSelector;

